'use client';

/* eslint-disable @next/next/no-img-element -- Footer island assets use the same SSR-safe native primitive as the server tree. */

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';

interface FooterAppsProps {
  androidEnabled: boolean;
  applicationLabel: string;
  buttonClassName: string;
  containerClassName: string;
  iconClassName: string;
  iosEnabled: boolean;
  labels: {
    android: string;
    ios: string;
    windows: string;
  };
  locale: string;
  textClassName: string;
  windowsButtonClassName: string;
}

const APP_ICONS = {
  android: '/images/icons/footer/app-android.svg',
  ios: '/images/icons/footer/app-apple.svg',
  windows: '/images/icons/footer/app-windows.svg',
} as const;

export const FooterApps = ({
  androidEnabled,
  applicationLabel,
  buttonClassName,
  containerClassName,
  iconClassName,
  iosEnabled,
  labels,
  locale,
  textClassName,
  windowsButtonClassName,
}: FooterAppsProps) => {
  const [isSafari, setIsSafari] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const installationGuidePath = `/${locale}/installation-guide`;
  const isInstallationGuide = pathname === installationGuidePath;

  useEffect(() => {
    setIsSafari(/^((?!chrome|android).)*safari/i.test(navigator.userAgent));
  }, []);

  const navigateToInstallationGuide = (enabled: boolean) => () => {
    if (enabled || !isInstallationGuide) {
      router.push('/installation-guide');
    }
  };

  const appButton = (
    platform: 'android' | 'ios',
    enabled: boolean,
    hidden = false
  ) =>
    hidden ? null : (
      <button
        className={buttonClassName}
        disabled={(platform === 'android' && !enabled) || isInstallationGuide}
        onClick={navigateToInstallationGuide(enabled)}
        type="button"
        data-analytics-id={`footer_app_${platform}`}
      >
        <img
          alt={`${platform} app`}
          className={iconClassName}
          height={30}
          loading="lazy"
          src={APP_ICONS[platform]}
          width={30}
        />
        <span className={textClassName}>
          <span>{applicationLabel}</span>
          <span>{labels[platform]}</span>
        </span>
      </button>
    );

  return (
    <section className={containerClassName} data-footer-block="apps">
      {appButton('ios', iosEnabled)}
      {appButton('android', androidEnabled, isSafari)}
      <button
        className={`${buttonClassName} ${windowsButtonClassName}`}
        disabled
        type="button"
        data-analytics-id="footer_app_windows"
      >
        <img
          alt="Windows app"
          className={iconClassName}
          height={31}
          loading="lazy"
          src={APP_ICONS.windows}
          width={30}
        />
        <span className={textClassName}>
          <span>{applicationLabel}</span>
          <span>{labels.windows}</span>
        </span>
      </button>
    </section>
  );
};
